import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { postApiWithAuth } from "../../../services/api/api"; // Matches the standard project API utility helper path

const ResetPasswordTab = () => {
    const navigate = useNavigate();

    // Core Input States
    const [formData, setFormData] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    // Validation & View Indicator Feedback Status States
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    // Field-Specific Password Eye Toggles
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Form Field Change Handlers Interceptor
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Dynamic clear validation errors logs while user updates values live
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
        if (errors.server) {
            setErrors({ ...errors, server: "" });
        }
    };

    // Client-Side Rigid Passwords Quality Constraint Evaluation Matrix Rule Engine
    const validateFieldsForm = () => {
        let tempErrors = {};

        // 1. Old Password Presence Verification Check
        if (!formData.oldPassword.trim()) {
            tempErrors.oldPassword = "Your current old password is required to verify ownership.";
        }

        // 2. New Password Structural Strength RegEx Expression Verification Rule
        // Rule Mapping constraints requirement: 1 Uppercase, 1 Lowercase, 1 Number, 1 Special character, Min 8 characters long
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

        if (!formData.newPassword) {
            tempErrors.newPassword = "A new replacement password string is required.";
        } else if (!strongPasswordRegex.test(formData.newPassword)) {
            tempErrors.newPassword = "Password must be at least 8 characters long and contain a combination of at least one uppercase letter, one lowercase letter, one numeric digit, and one special character (e.g. @, $, !, %, *, ?, &, #).";
        }

        // 3. Password Confirmation Matching Verification Check
        if (!formData.confirmPassword) {
            tempErrors.confirmPassword = "Please repeat your new password configuration to confirm accuracy.";
        } else if (formData.confirmPassword !== formData.newPassword) {
            tempErrors.confirmPassword = "Confirmation mapping mismatch. Passwords do not match.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // Network Request Processing Handler
    const handlePasswordChangeSubmit = async (e) => {
        e.preventDefault();
        if (!validateFieldsForm()) return;

        setLoading(true);
        setSuccessMsg("");
        setErrors({});
        let mobileNumber = "";
        try {
            const userDetailsStr = localStorage.getItem("userDetails");
            if (userDetailsStr) {
                const details = JSON.parse(userDetailsStr);
                mobileNumber = details.mobile || "";
            }
        } catch (err) {
            console.error("Local storage user details parsing error context:", err);
        }
        try {
            // Execute the authorized network transaction via API helper
            const response = await postApiWithAuth("/reset-password", {
                mobile: mobileNumber,
                old_password: formData.oldPassword,
                new_password: formData.newPassword,
                new_password_confirmation: formData.confirmPassword
            });

            if (response && (response.success || response.code === 200 || response.status === 200)) {
                setSuccessMsg("Your password has been securely updated! Cleared local active session footprints. Redirecting to landing portal...");
                setFormData({ oldPassword: "", newPassword: "", confirmPassword: "" });

                // Delay checkout actions brief moment to allow user reading alert panel layout
                setTimeout(() => {
                    // PURGE SESSION LOOPS CACHEfootprints completely
                    localStorage.clear();
                    sessionStorage.clear();
                    navigate("/", { replace: true });
                    window.location.reload();
                }, 3000);
            } else {
                setErrors({ server: response?.message || "Failed to modify password parameters. Please confirm original password metrics." });
            }
        } catch (error) {
            console.error("Password modification network exception log trace:", error);
            setErrors({ server: error.response?.data?.message || "An unexpected error occurred during execution transaction. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tab-content-box">
            <div className="row">
                <div className="col-lg-12">
                    <div className="info-card">
                        <div className="log_re_heading">
                            <h2>Change Password</h2>
                            <p>Keep your account safe by changing your password regularly.</p>
                        </div>

                        {/* Top Context Notification Alert Feedback Blocks Rendering Rows */}
                        {successMsg && <div className="alert alert-success p-3 small mb-3 fw-bold">✓ {successMsg}</div>}
                        {errors.server && <div className="alert alert-danger p-3 small mb-3">⚠️ {errors.server}</div>}

                        <form onSubmit={handlePasswordChangeSubmit}>
                            <div className="row">

                                {/* 1. Old Password Field */}
                                <div className='col-md-12'>
                                    <div className="inputcontainer mb-3">
                                        <h5 className="input_label">Old Password<span className="m_feild">*</span></h5>
                                        <div className="position-relative">
                                            <input
                                                type={showOldPassword ? "text" : "password"}
                                                name="oldPassword"
                                                className={`form-control input_style ${errors.oldPassword ? "is-invalid" : ""}`}
                                                placeholder="Enter Old Password"
                                                value={formData.oldPassword}
                                                onChange={handleInputChange}
                                                style={{ paddingRight: "45px" }}
                                            />
                                            <span
                                                onClick={() => setShowOldPassword(!showOldPassword)}
                                                style={{ position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: "16px", cursor: "pointer", zIndex: 10 }}
                                            >
                                                <i className={`fa ${showOldPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                            </span>
                                        </div>
                                        {errors.oldPassword && <div className="text-danger small mt-1" style={{ fontSize: "12px" }}>{errors.oldPassword}</div>}
                                    </div>
                                </div>

                                {/* 2. New Password Field */}
                                <div className='col-md-12'>
                                    <div className="inputcontainer mb-3">
                                        <h5 className="input_label">New Password<span className="m_feild">*</span></h5>
                                        <div className="position-relative">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                name="newPassword"
                                                className={`form-control input_style ${errors.newPassword ? "is-invalid" : ""}`}
                                                placeholder="Enter New Password"
                                                value={formData.newPassword}
                                                onChange={handleInputChange}
                                                style={{ paddingRight: "45px" }}
                                            />
                                            <span
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                style={{ position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: "16px", cursor: "pointer", zIndex: 10 }}
                                            >
                                                <i className={`fa ${showNewPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                            </span>
                                        </div>
                                        {errors.newPassword && <div className="text-danger small mt-1" style={{ fontSize: "12px", lineHeight: "1.4" }}>{errors.newPassword}</div>}
                                    </div>
                                </div>

                                {/* 3. Confirm New Password Field */}
                                <div className='col-md-12'>
                                    <div className="inputcontainer mb-4">
                                        <h5 className="input_label">Confirm New Password<span className="m_feild">*</span></h5>
                                        <div className="position-relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                className={`form-control input_style ${errors.confirmPassword ? "is-invalid" : ""}`}
                                                placeholder="Enter Confirm New Password"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                style={{ paddingRight: "45px" }}
                                            />
                                            <span
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{ position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: "16px", cursor: "pointer", zIndex: 10 }}
                                            >
                                                <i className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                            </span>
                                        </div>
                                        {errors.confirmPassword && <div className="text-danger small mt-1" style={{ fontSize: "12px" }}>{errors.confirmPassword}</div>}
                                    </div>
                                </div>

                                {/* Action Submit Command Processing Trigger Node Button */}
                                <div className="col-md-3">
                                    <button
                                        type="submit"
                                        className="btn next-btn w-100"
                                        disabled={loading || !!successMsg}
                                    >
                                        {loading ? "Processing..." : "Change Password"}
                                    </button>
                                </div>

                            </div>
                        </form>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordTab;